<template>
  <div class="border-0 scroll-container px-1">

    <!-- Navigation between sections -->
    <div class="d-flex justify-content-end mb-2">
    </div>

    <!-- Help Panel -->
    <div v-if="show_page_help" class="alert alert-info border rounded-lg mb-2 d-flex align-items-start gap-2 shadow-sm">
      <i class="bi bi-lightbulb-fill text-info fs-5 mt-1"></i>
      <div>
        <strong class="text-xs">About This Page</strong>
        <p class="text-xxs mb-0 mt-1">Fill in your profile form answers here. The fields marked with identity will be synced with your account details.</p>
      </div>
      <button type="button" class="btn-close ms-auto" @click="show_page_help = false" aria-label="Close"></button>
    </div>

    <!-- Loading -->
    <div v-if="is_loading" class="glassy-table border rounded-lg p-5 text-center mb-2 shadow-sm">
      <div class="spinner-border text-primary mx-auto" role="status"></div>
      <p class="text-muted mt-3 mb-0 text-xxs">Loading profile form...</p>
    </div>

    <template v-else>
      <!-- Profile Form -->
      <div class="glassy-table border rounded-lg mb-2 shadow-sm">
        <div class="card-header d-flex justify-content-between align-items-center">
          <div class="d-flex align-items-center gap-2">
            <i class="bi bi-journal-text text-success"></i>
            <h6 class="mb-0 fw-bold text-xs">Profile Information</h6>
          </div>
          <div class="d-flex align-items-center gap-2">
            <span v-if="is_editing" class="text-muted text-xs">{{ save_message }}</span>
            <button v-if="is_editing"
                    class="btn btn-primary-dark btn-sm text-xs shadow-sm text-dark"
                    @click="update_profile" :disabled="is_saving">
              <span v-if="is_saving" class="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
              Update Form
            </button>
            <button v-else
                    class="btn btn-primary-dark btn-sm text-xs shadow-sm text-dark"
                    @click="is_editing = true">
              Edit Form
            </button>
          </div>
        </div>
        <div class="card-body p-4">

          <!-- No form assigned -->
          <div v-if="!has_profile_form" class="text-center py-5">
            <i class="bi bi-file-earmark-x fs-1 text-muted opacity-50"></i>
            <h6 class="mt-3 mb-1 fw-bold text-dark">No Profile Form Assigned</h6>
            <p class="text-muted text-xs mb-0">A profile form has not been assigned to your account yet.</p>
          </div>

          <!-- Form questions -->
          <div v-else class="row g-3">
            <div v-for="question in profile_questions" :key="question.id"
                 :class="(question.question_type === 'single_select' || question.question_type === 'multi_select') ? 'col-12' : 'col-md-6'">

              <label class="fw-7 text-xs text-muted mb-1 d-block">{{ question.label }}</label>

              <!-- Long text -->
              <textarea v-if="question.question_type === 'long_text'"
                class="form-control form-control-sm text-xs bg-light border py-1 rounded-lg"
                rows="3" :disabled="!is_editing" v-model="question.editable_value"
                placeholder="Enter your response..."></textarea>

              <!-- Single select — Radio buttons -->
              <div v-else-if="question.question_type === 'single_select'" class="mt-1">
                <div v-if="question.options && question.options.length" class="d-flex flex-wrap gap-3">
                  <div v-for="(opt, optIdx) in question.options"
                       :key="'ss-' + question.id + '-' + optIdx"
                       class="form-check">
                    <input class="form-check-input" type="radio"
                           :name="'q-' + question.id"
                           :id="'q-' + question.id + '-opt-' + optIdx"
                           :value="opt.value"
                           v-model="question.editable_value"
                           :disabled="!is_editing">
                    <label class="form-check-label text-xs"
                           :for="'q-' + question.id + '-opt-' + optIdx">
                      {{ opt.text }}
                    </label>
                  </div>
                </div>
                <p v-else class="text-muted text-xs mb-0 fst-italic">No options available</p>
              </div>

              <!-- Multi select — Checkboxes -->
              <div v-else-if="question.question_type === 'multi_select'" class="mt-1">
                <div v-if="question.options && question.options.length" class="d-flex flex-wrap gap-3">
                  <div v-for="(opt, optIdx) in question.options"
                       :key="'ms-' + question.id + '-' + optIdx"
                       class="form-check">
                    <input class="form-check-input" type="checkbox"
                           :id="'q-' + question.id + '-opt-' + optIdx"
                           :value="opt.value"
                           v-model="question.editable_value"
                           :disabled="!is_editing">
                    <label class="form-check-label text-xs"
                           :for="'q-' + question.id + '-opt-' + optIdx">
                      {{ opt.text }}
                    </label>
                  </div>
                </div>
                <p v-else class="text-muted text-xs mb-0 fst-italic">No options available</p>
              </div>

              <!-- Dropdown -->
              <select v-else-if="question.question_type === 'dropdown'"
                class="form-control form-control-sm text-xs bg-light border py-1 rounded-lg"
                :disabled="!is_editing" v-model="question.editable_value">
                <option value="">Select an option</option>
                <option v-for="(opt, optIdx) in question.options"
                        :key="'dd-' + question.id + '-' + optIdx"
                        :value="opt.value">{{ opt.text }}</option>
              </select>

              <!-- Date -->
              <input v-else-if="question.question_type === 'date'"
                class="form-control form-control-sm text-xs bg-light border py-1 rounded-lg"
                type="date" :disabled="!is_editing" v-model="question.editable_value">

              <!-- Number -->
              <input v-else-if="question.question_type === 'number'"
                class="form-control form-control-sm text-xs bg-light border py-1 rounded-lg"
                type="number" :disabled="!is_editing" v-model="question.editable_value"
                placeholder="Enter a number...">

              <!-- Default text input -->
              <input v-else
                class="form-control form-control-sm text-xs bg-light border py-1 rounded-lg"
                :type="question.question_type === 'email' ? 'email' : 'text'"
                :disabled="!is_editing" v-model="question.editable_value"
                placeholder="Enter your response...">
            </div>
          </div>

          <!-- Work Experience Blocks -->
          <div v-if="work_experience_blocks.length" class="mt-4">
            <div class="d-flex align-items-center gap-2 mb-3">
              <i class="bi bi-briefcase text-primary"></i>
              <h6 class="mb-0 fw-bold text-xs">Work Experience</h6>
            </div>
            <div v-for="(block, bidx) in work_experience_blocks" :key="'we-pf-' + bidx"
                 class="we-block-card mb-3 p-3 border border-secondary-subtle rounded-3 bg-white shadow-sm">
              <div class="d-flex justify-content-between align-items-center mb-3 pb-2 border-bottom">
                <p class="fw-bold text-xs mb-0 text-dark">
                  <i class="bi bi-building me-1 text-primary"></i>
                  {{ block.label }}
                </p>
                <button v-if="is_editing && work_experience_blocks.length > 1"
                  class="btn btn-outline-danger btn-sm text-xxs py-0 px-2"
                  @click="remove_we_block(bidx)">
                  <i class="bi bi-trash me-1"></i>Remove
                </button>
              </div>
              <div class="row g-3">
                <template v-for="(field, fidx) in block.fields" :key="'we-f-' + bidx + '-' + fidx">
                  <div :class="['From Month','From Year','To Month','To Year'].includes(field.label) ? 'col-6 col-md-3' : (field.label === 'Reason for Leaving' ? 'col-12' : 'col-md-6')">
                    <label class="form-label fw-medium text-xxs text-muted text-uppercase mb-1 d-block">{{ field.label }}</label>
                    <select v-if="field.field_type === 'we_month'"
                      class="form-select form-select-sm text-xs rounded-lg"
                      v-model="field.editable_value" :disabled="!is_editing">
                      <option value="" disabled>Month</option>
                      <option v-for="(monthLabel, monthIndex) in ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec']" :key="'mo-'+(monthIndex + 1)" :value="String(monthLabel)">{{ monthLabel }}</option>
                    </select>
                    <select v-else-if="field.field_type === 'we_year'"
                      class="form-select form-select-sm text-xs rounded-lg"
                      v-model="field.editable_value" :disabled="!is_editing">
                      <option value="" disabled>Year</option>
                      <option v-for="y in 57" :key="'yr-'+(2027-y)" :value="String(2027-y)">{{ 2027 - y }}</option>
                    </select>
                    <textarea v-else-if="field.field_type === 'textarea'"
                      class="form-control form-control-sm text-xs bg-light border py-1 rounded-lg"
                      v-model="field.editable_value" rows="2" maxlength="200"
                      placeholder="Reason for leaving" :disabled="!is_editing"></textarea>
                    <input v-else type="text"
                      class="form-control form-control-sm text-xs bg-light border py-1 rounded-lg"
                      v-model="field.editable_value" :placeholder="field.label" :disabled="!is_editing" />
                  </div>
                </template>
              </div>
            </div>
            <div v-if="is_editing && work_experience_blocks.length < (_all_we_blocks || []).length && work_experience_blocks.length < 3"
                 class="text-center mt-2 mb-3">
              <button class="btn btn-outline-secondary btn-sm text-xs rounded-pill px-4"
                      @click="add_we_block">
                <i class="bi bi-plus-circle me-1"></i>Add Experience
              </button>
            </div>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>